import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getAutocompleteAddresses, getLocationByPlaceid } from "../../../actions/googleAPI";
import AirportLogoMaps from "../../../images/airport-logo-maps.svg";
import PointLogoMaps from "../../../images/point-logo-maps.svg";
import StationLogoMaps from "../../../images/station-logo-maps.svg";
import HotelLogoMaps from "../../../images/hotel-logo-maps.svg";
import RestaurantLogoMaps from "../../../images/restaurant-logo-maps.svg";
import { cn } from "../../../utils/classnames";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { DURATION, EASE_OUT } from "../../motion";
import { playSelectedRing } from "../form-card/formMotion";
import { BROWSER_AUTOFILL_OFF } from "../../../utils/autofill";

// Each suggestion rises in this long after the one above it.
const SUGGESTION_STAGGER_SECONDS = 0.035;

// The thin rule between suggestions. It used to be an <hr> after each row; it
// is a border now because AnimatePresence can only animate the rows
// themselves, and a loose <hr> would be left behind when a row fades out.
const SUGGESTION_DIVIDER = "1px solid rgba(0, 0, 0, 0.25)";

// Placeholder rows shown while suggestions load, shaped like the real rows
// (icon, title, address) so the list doesn't jump when they arrive.
const SUGGESTION_SKELETON_ROW_COUNT = 3;
// It fades in and out (no movement) so swapping it for the real rows reads
// as one cross-fade. forwardRef is required by AnimatePresence's popLayout.
const SuggestionSkeletonRow = React.forwardRef((props, ref) => (
  <motion.li
    ref={ref}
    className="tw:flex tw:items-center tw:gap-3 tw:px-4 tw:py-3 tw:animate-pulse"
    aria-hidden="true"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: DURATION.fast, ease: EASE_OUT }}
  >
    <span className="tw:h-6 tw:w-6 tw:shrink-0 tw:rounded-full tw:bg-[#F1F1F1]" />
    <span className="tw:flex tw:flex-1 tw:flex-col tw:gap-1.5">
      <span className="tw:h-3 tw:w-2/5 tw:rounded tw:bg-[#F1F1F1]" />
      <span className="tw:h-2.5 tw:w-3/5 tw:rounded tw:bg-[#F1F1F1]" />
    </span>
  </motion.li>
));

// Small chip next to an airport suggestion that came from a terminal search,
// e.g. "Haneda Airport (HND)  [Terminal 3 (International)]", so the customer
// can see the terminal was understood.
const TerminalBadge = ({ terminalLabel }) => {
  const { t } = useTranslation();
  return (
    <span className="tw:inline-flex tw:items-center tw:rounded-full tw:bg-[#F1F1F1] tw:px-2 tw:py-0.5 tw:text-[11px] tw:font-medium tw:leading-4 tw:text-[#4E4E4E]">
      {t(`BookingConfirmation.TerminalList.${terminalLabel}`)}
    </span>
  );
};

const CustomGoogleAutoComplete = ({
  id,
  isInvalid = false,
  invalidText,
  onPlaceSelected,
  defaultValue = null,
  placeholder,
  ariaLabel,
  debounceMs = 500,
  disabled = false,
  inputClassName,
  inputStyle,
}) => {
  const { t } = useTranslation();
  const [selectedPickupTestLocation, setSelectedPickupTestLocation] = useState(null);
  const [pickupTestList, setPickupTestList] = useState([]);
  const [focused, setFocused] = useState(false);
  const [typedLocation, setTypedLocation] = useState("");
  const [filled, setFilled] = useState(defaultValue !== null ? true : false);
  const [isLoading, setIsLoading] = useState(false)
  const shouldReduceMotion = useReducedMotion()

  // Ref to store the timeout ID for debouncing
  const debounceTimeoutRef = useRef(null)
  const inputRef = useRef(null)

  // Gold ring on the input once a place is picked (shared with date/time).
  const playSelectedRingOnInput = () => playSelectedRing(inputRef.current, shouldReduceMotion)

  const onFocus = () => setFocused(true)
  const onBlur = () => setFocused(false)

  // Debounced search function
  const debouncedSearch = async (searchTerm) => {
    if (searchTerm.trim().length === 0) {
      setPickupTestList([])
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      const list = await getAutocompleteAddresses(searchTerm)
      setPickupTestList(list)
    } catch (error) {
      console.error("Error fetching autocomplete addresses:", error)
      setPickupTestList([])
    } finally {
      setIsLoading(false)
    }
  }

  const setSearchLocation = async (loc) => {
    setTypedLocation(loc)

    // Clear the previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    // Set a new timeout for debouncing
    debounceTimeoutRef.current = setTimeout(() => {
      debouncedSearch(loc)
    }, debounceMs)
    // if (loc.trim().length === 0) {
    //   setPickupTestList([]);
    // } else {
    //   const list = await getAutocompleteAddresses(loc);

    //   setPickupTestList(list);
    // }
  }

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [])

  const handleSelectPickupTestLocation = async (e) => {
    // Close the list and show the choice straight away; the place details
    // below take a moment to load and the list used to hang open meanwhile.
    setTypedLocation(e.description)
    setSelectedPickupTestLocation(e.description)
    setPickupTestList([])
    setFilled(true)
    playSelectedRingOnInput()

    let detailLocation = await getLocationByPlaceid(e.place_id)
    detailLocation.name = e?.structured_formatting?.main_text

    // A terminal search ("haneda terminal 3") comes back from the server as
    // the airport itself plus the terminal. Keep the terminal on the location
    // so the booking page can start on it.
    if (e?.terminal) {
      detailLocation.terminal = e.terminal
    }

    if (!detailLocation.formatted_address.startsWith(e?.structured_formatting?.main_text)) {
      detailLocation.formatted_address = `${e?.structured_formatting?.main_text}, ${detailLocation.formatted_address}`
    }

    onPlaceSelected(detailLocation);
  }

  const getLocationIcon = (types) => {
    if (types.includes("airport")) return AirportLogoMaps
    if (types.includes("lodging")) return HotelLogoMaps
    if (types.includes("train_station")) return StationLogoMaps
    if (types.includes("restaurant")) return RestaurantLogoMaps
    return PointLogoMaps
  }

  const getInputValue = () => {
    if (focused) return typedLocation
    if (selectedPickupTestLocation !== null) return selectedPickupTestLocation
    if (defaultValue !== null) return defaultValue
    return typedLocation
  }

  const shouldShowDropdown = typedLocation.length !== 0 && (pickupTestList.length > 0 || isLoading)
  return (
    <>
      <input
        type="text"
        className={cn(inputClassName, "form-control fw-700 form-control-icon tw:!h-[50px]", isInvalid ? 'invalid' : "")}
        onChange={(e) =>
          setSearchLocation(e.target.value)
        }
        ref={inputRef}
        id={id}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onFocus={onFocus}
        onBlur={onBlur}
        autoComplete={BROWSER_AUTOFILL_OFF}
        value={getInputValue()}
        style={{
          color: filled ? "#282828 !important" : "",
          fontSize: "14px",
          ...inputStyle,
        }}
      />

      {isInvalid && (
        <p className="text-danger">
          {t(invalidText)}
        </p>
      )}

      {/* The list drops in under the input and fades out once a place is
          picked; each suggestion rises in just after the one above it. */}
      <AnimatePresence>
        {shouldShowDropdown && (
          <motion.ul
            key="location-suggestions"
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: shouldReduceMotion ? 0 : -6, pointerEvents: "none" }}
            transition={{ duration: DURATION.fast, ease: EASE_OUT }}
            style={{
              position: "absolute",
              padding: '0',
              margin: '0',
              backgroundColor: 'white', /* Sesuaikan dengan warna latar belakang yang Anda inginkan */
              border: '1px solid #ccc', /* Atur border sesuai kebutuhan */
              zIndex: '1000', /* Sesuaikan dengan kebutuhan, pastikan lebih tinggi dari z-index konten lainnya */
              width: 'calc(100% - 2px)', /* Biarkan lebar ul sama dengan lebar input text */
            }}
          >
            {/* popLayout lifts outgoing rows out of the flow while they fade,
                so the skeleton cross-fades into the results and a new search
                swaps rows without the list jumping. Rows that appear in both
                searches keep their key and simply stay put. */}
            <AnimatePresence initial={false} mode="popLayout">
              {
                isLoading ? (
                  Array.from({ length: SUGGESTION_SKELETON_ROW_COUNT }, (_, skeletonIndex) => (
                    <SuggestionSkeletonRow key={`skeleton-${skeletonIndex}`} />
                  ))
                ) : (
                  pickupTestList?.map((location, suggestionIndex) => (
                    // Haneda itself and "Haneda, Terminal 3" share a place_id.
                    <motion.li
                      key={`${location.place_id}-${location.terminalLabel || ""}`}
                      className="dropdown-item m-0"
                      style={{ borderBottom: SUGGESTION_DIVIDER }}
                      initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      // A small press-in so the tap on a suggestion registers.
                      whileTap={shouldReduceMotion ? undefined : { scale: 0.98 }}
                      transition={{
                        duration: DURATION.fast,
                        ease: EASE_OUT,
                        delay: shouldReduceMotion ? 0 : suggestionIndex * SUGGESTION_STAGGER_SECONDS,
                      }}
                      onClick={(e) =>
                        handleSelectPickupTestLocation(location)
                      }
                    >
                      <div className="row px-1 align-items-center" style={{ whiteSpace: 'break-spaces' }}>
                        <div className="col-1 d-flex align-items-center justify-content-center tw:min-w-[48px]">
                          <img
                            src={getLocationIcon(location.types)}
                            className="text-center"
                            alt="Airport icon"
                          />
                        </div>
                        {/* Title and address are block-level with their own tight
                            line heights, so the gap between them stays small. */}
                        <div className="col ps-0">
                          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-x-2 tw:gap-y-0.5">
                            <span className="place-name tw:!leading-5">{location?.structured_formatting?.main_text}</span>
                            {location?.terminalLabel && (
                              <TerminalBadge terminalLabel={location.terminalLabel} />
                            )}
                          </div>
                          <span className="place-city-name tw:block tw:!leading-4">
                            {location?.structured_formatting?.secondary_text?.replace(", Japan", "")}
                          </span>
                        </div>
                      </div>
                    </motion.li>
                  ))
                )
              }
            </AnimatePresence>
          </motion.ul>
        )}
      </AnimatePresence>
    </>
  );
};

export default CustomGoogleAutoComplete;